/**
 * A table's column layout: which columns a reader shows, and in what order.
 *
 * Pure, and deliberately React-free — `src/AGENTS.md` keeps React out of
 * `src/lib/`. The React half is `src/hooks/use-column-preference.ts`, which
 * loads and saves; the rendering half is
 * `src/design-system/column-picker.tsx`. Everything decided here is decided
 * from a preset and a column list, which is what lets each rule below be
 * tested as an input and an output rather than through a rendered screen.
 *
 * The preset began with core-service's `{ order, hidden }` shape. It now adds
 * optional `frozen` and `widths` keys for this app. A future shared endpoint
 * must accept or strip those keys. `src/lib/column-preference-store.ts` owns
 * the local storage.
 *
 * **Layout is not visibility.** `order` lists every column the reader has
 * placed and `hidden` lists the ones they have switched off, and a column can
 * be in both — the picker shows a hidden column in its place in the list, so
 * switching it back on returns it where the reader left it rather than to the
 * end. That is why `laidOutColumns` hides nothing and `visibleColumns` is a
 * filter over it.
 */

/**
 * The most column keys either stored list may carry.
 *
 * core-service answers 400 above this, and the cap is kept here although
 * nothing local enforces one: the stored shape is that endpoint's, and a
 * layout this app writes must be one it would accept. It also bounds what a
 * single key may grow to.
 */
export const MAX_COLUMN_KEYS = 200
/** The most columns a reader can keep at the left edge. */
export const MAX_FROZEN_COLUMNS = 3
/** Bounds for saved column widths, in pixels. */
export const MIN_COLUMN_WIDTH = 64
export const MAX_COLUMN_WIDTH = 800

/** Keep a column width in the supported range and round it to whole pixels.
 * @param width - The requested width.
 * @returns The bounded width.
 */
export function clampColumnWidth(width: number): number {
  return Math.round(
    Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, width))
  )
}

/** One table's stored column layout. Both lists are optional and may be stale. */
export interface TableColumnPreset {
  /** Column keys in the order the reader placed them. May name only some. */
  order?: readonly string[]
  /** Column keys the reader has switched off. */
  hidden?: readonly string[]
  /** Column keys pinned to the left. Their order follows `order`. */
  frozen?: readonly string[]
  /** Explicit column widths in pixels; absent means automatic width. */
  widths?: Readonly<Record<string, number>>
}

/**
 * The least a column must carry to be laid out.
 *
 * A structural type rather than `DataTableColumn`, so this module never needs
 * the render function — and so the picker, which only ever shows a name, can
 * pass the same list.
 */
export interface ColumnIdentity {
  key: string
  label: string
}

/**
 * The keys the app actually declares, which is what every list is filtered to.
 *
 * A stored preset outlives the screen that wrote it. A column removed since —
 * or renamed — leaves a key behind that names nothing, and laying that key out
 * would render a header with no cells under it.
 *
 * @param columns - The screen's declared columns.
 * @returns The declared keys.
 */
function declaredKeys(columns: readonly ColumnIdentity[]): Set<string> {
  return new Set(columns.map(column => column.key))
}

/**
 * A stored list as the declared keys it names, once each, in the order given.
 *
 * Both filters matter. The unknown key is the stale one above; the duplicate
 * is what would otherwise render one column twice and hand React two children
 * under a single key.
 *
 * @param keys - A stored list, which may be absent, stale or repetitive.
 * @param declared - The keys the app declares.
 * @returns The usable keys, in order, without repeats.
 */
function usableKeys(
  keys: readonly string[] | undefined,
  declared: ReadonlySet<string>
): string[] {
  const seen = new Set<string>()

  return (keys ?? []).filter(key => {
    if (!declared.has(key) || seen.has(key)) {
      return false
    }
    seen.add(key)

    return true
  })
}

/**
 * Every column, in the reader's order.
 *
 * Columns the preset places come first, in the order it places them; the rest
 * follow in the order the screen declared them. A partial `order` is the
 * normal case, not an edge one — a reader who drags one column to the front
 * has placed exactly one, and the other fourteen must still render, in the
 * order the screen meant them to.
 *
 * Nothing is hidden here. See the note at the top of this file.
 *
 * @param columns - The screen's declared columns.
 * @param preset - The reader's stored layout.
 * @returns Every column, in the reader's order.
 */
export function laidOutColumns<Column extends ColumnIdentity>(
  columns: readonly Column[],
  preset: TableColumnPreset
): Column[] {
  const placed = usableKeys(preset.order, declaredKeys(columns))
  const byKey = new Map(columns.map(column => [column.key, column]))
  // Every key in `placed` is declared, so every lookup hits — the filter is
  // what tells the compiler so, without an assertion that would also hide a
  // real miss if `usableKeys` ever stopped filtering.
  const placedColumns = placed
    .map(key => byKey.get(key))
    .filter((column): column is Column => column !== undefined)
  const placedSet = new Set(placed)

  const ordered = [
    ...placedColumns,
    ...columns.filter(column => !placedSet.has(column.key))
  ]
  const frozen = frozenSet(columns, preset)
  return [
    ...ordered.filter(column => frozen.has(column.key)),
    ...ordered.filter(column => !frozen.has(column.key))
  ]
}

/**
 * The frozen keys that take effect: declared, shown, and within the cap.
 *
 * @param columns - The screen's declared columns.
 * @param preset - The reader's stored layout.
 * @returns The effective frozen keys, in the stored list's order.
 */
function frozenSet(
  columns: readonly ColumnIdentity[],
  preset: TableColumnPreset
): Set<string> {
  const hidden = new Set(preset.hidden ?? [])

  return new Set(
    usableKeys(preset.frozen, declaredKeys(columns))
      .filter(key => !hidden.has(key))
      .slice(0, MAX_FROZEN_COLUMNS)
  )
}

/**
 * The columns pinned to the left edge, in the order the table draws them.
 *
 * The one answer to "is this column frozen". A stored list may name a column
 * the screen dropped, a column the reader hid, or more columns than the cap,
 * and every reader of the list must skip the same ones.
 *
 * @param columns - The screen's declared columns.
 * @param preset - The reader's stored layout.
 * @returns The frozen keys, in laid-out order.
 */
export function frozenKeys(
  columns: readonly ColumnIdentity[],
  preset: TableColumnPreset
): string[] {
  const frozen = frozenSet(columns, preset)

  return laidOutColumns(columns, preset)
    .map(column => column.key)
    .filter(key => frozen.has(key))
}

/**
 * The hidden keys the app still declares.
 *
 * @param columns - The screen's declared columns.
 * @param preset - The reader's stored layout.
 * @returns The keys that are both declared and switched off.
 */
export function hiddenSet(
  columns: readonly ColumnIdentity[],
  preset: TableColumnPreset
): Set<string> {
  return new Set(usableKeys(preset.hidden, declaredKeys(columns)))
}

/**
 * The columns the table renders, in the reader's order.
 *
 * **Never empty.** A preset that hides every column would otherwise render a
 * card with no headers and no cells, and the picker that would switch one back
 * on is driven by this same list — so the reader would have nothing to press.
 * `toggleColumn` refuses to reach that state, but a bucket edited by another
 * client, or by hand, does not go through `toggleColumn`. The first column in
 * the reader's own order is the one that survives, not the first declared one:
 * a reader who dragged a column to the front chose it as the important one.
 *
 * @param columns - The screen's declared columns.
 * @param preset - The reader's stored layout.
 * @returns The columns to render, in order, with at least one in the list.
 */
export function visibleColumns<Column extends ColumnIdentity>(
  columns: readonly Column[],
  preset: TableColumnPreset
): Column[] {
  const laidOut = laidOutColumns(columns, preset)
  // The raw stored list, not `hiddenSet`: everything in `laidOut` is declared
  // by construction, so a stale key simply never matches and filtering it out
  // first would build a second `declaredKeys` set for no answer it changes.
  const hidden = new Set(preset.hidden ?? [])
  const shown = laidOut.filter(column => !hidden.has(column.key))

  // `laidOut` is empty only when the screen itself declares no columns, which
  // is a screen with no table. Slicing answers `[]` for it rather than throwing.
  return shown.length > 0 ? shown : laidOut.slice(0, 1)
}

/**
 * The reader's layout, pruned and bounded, ready to store.
 *
 * Three jobs, all of them about what the endpoint will accept and what the
 * document will hold a year from now. Stale keys are dropped, so a screen that
 * loses a column does not carry its name forever. An empty list is dropped
 * rather than stored as `[]`, so a reader who has changed nothing writes `{}`.
 * And each list is capped at what core-service validates, so a layout written
 * here stays one that endpoint would accept.
 *
 * @param columns - The screen's declared columns.
 * @param preset - The layout to store.
 * @returns The preset to send, carrying only the lists that hold something.
 */
export function presetForStorage(
  columns: readonly ColumnIdentity[],
  preset: TableColumnPreset
): TableColumnPreset {
  const declared = declaredKeys(columns)
  const order = usableKeys(preset.order, declared).slice(0, MAX_COLUMN_KEYS)
  const hidden = usableKeys(preset.hidden, declared).slice(0, MAX_COLUMN_KEYS)
  const frozen = frozenKeys(columns, preset)
  const widths = Object.fromEntries(
    Object.entries(preset.widths ?? {})
      .filter(([key, width]) => declared.has(key) && Number.isFinite(width))
      .slice(0, MAX_COLUMN_KEYS)
      .map(([key, width]) => [key, clampColumnWidth(width)])
  )

  return {
    ...(order.length > 0 ? { order } : {}),
    ...(hidden.length > 0 ? { hidden } : {}),
    ...(frozen.length > 0 ? { frozen } : {}),
    ...(Object.keys(widths).length > 0 ? { widths } : {})
  }
}

/**
 * Switch one column on or off.
 *
 * Refuses to switch off the last one that is showing: a table with no columns
 * is unreadable and unrecoverable from the picker, so the guard belongs at the
 * point of the press rather than in a banner afterwards. The press simply does
 * nothing, and the picker disables that row's control so the reader is never
 * offered it.
 *
 * The order is left exactly as it was. Switching a column off does not move
 * it, which is what lets switching it back on return it to its place.
 *
 * @param columns - The screen's declared columns.
 * @param preset - The reader's stored layout.
 * @param key - The column to switch.
 * @returns The new layout, or the old one when the change is refused.
 */
export function toggleColumn(
  columns: readonly ColumnIdentity[],
  preset: TableColumnPreset,
  key: string
): TableColumnPreset {
  const declared = declaredKeys(columns)
  if (!declared.has(key)) {
    return preset
  }

  const hidden = usableKeys(preset.hidden, declared)
  const wasHidden = hidden.includes(key)
  // The last one showing. Counted from the declared list rather than from the
  // rendered one, so the answer does not depend on what is on screen.
  if (!wasHidden && hidden.length >= columns.length - 1) {
    return preset
  }

  const next = wasHidden
    ? hidden.filter(hiddenKey => hiddenKey !== key)
    : [...hidden, key]
  const nextFrozen =
    !wasHidden && preset.frozen
      ? preset.frozen.filter(frozenKey => frozenKey !== key)
      : undefined

  return {
    ...preset,
    hidden: next.length > 0 ? next : undefined,
    ...(!wasHidden && preset.frozen
      ? { frozen: nextFrozen?.length ? nextFrozen : undefined }
      : {})
  }
}

/**
 * Pin or unpin a visible column without crossing the freeze cap.
 * @param columns - The screen's declared columns.
 * @param preset - The reader's stored layout.
 * @param key - The column to change.
 * @returns The changed layout, or the same preset when refused.
 */
export function toggleFrozen(
  columns: readonly ColumnIdentity[],
  preset: TableColumnPreset,
  key: string
): TableColumnPreset {
  if (!declaredKeys(columns).has(key) || hiddenSet(columns, preset).has(key))
    return preset
  const frozen = frozenKeys(columns, preset)
  if (frozen.includes(key)) {
    const order = laidOutColumns(columns, preset).map(column => column.key)
    const nextFrozen = frozen.filter(item => item !== key)
    const nextOrder = order.filter(item => item !== key)
    nextOrder.splice(nextFrozen.length, 0, key)
    return {
      ...preset,
      order: nextOrder,
      frozen: nextFrozen.length ? nextFrozen : undefined
    }
  }
  if (frozen.length >= MAX_FROZEN_COLUMNS) return preset
  const order = laidOutColumns(columns, preset).map(column => column.key)
  const nextOrder = order.filter(item => item !== key)
  nextOrder.splice(frozen.length, 0, key)
  return { ...preset, order: nextOrder, frozen: [...frozen, key] }
}

/**
 * Set or reset one declared column's width.
 * @param columns - The screen's declared columns.
 * @param preset - The reader's stored layout.
 * @param key - The column to change.
 * @param width - The width in pixels, or null to reset it.
 * @returns The changed layout, or the same preset for an undeclared key.
 */
export function setColumnWidth(
  columns: readonly ColumnIdentity[],
  preset: TableColumnPreset,
  key: string,
  width: number | null
): TableColumnPreset {
  if (!declaredKeys(columns).has(key)) return preset
  const widths = { ...preset.widths }
  if (width === null) delete widths[key]
  else widths[key] = clampColumnWidth(width)
  return { ...preset, widths }
}

/**
 * Move one column to a place in the reader's order.
 *
 * One target index, not a step: the picker renders the laid-out order and so
 * already holds every row's index, and a drop names an index outright. A
 * second "by a step" shape would be two call conventions for one move.
 *
 * The index is clamped rather than wrapped. No caller can exceed the range
 * today — the picker passes the index of a row it is rendering — but this is
 * exported and takes a raw number, so a column landing at the far end of the
 * list because a caller was one out is a defect the caller cannot see.
 *
 * Always writes the WHOLE order, not just the moved key. A partial order is
 * read correctly — `laidOutColumns` says how — but it makes the next move
 * depend on the declared order as well as the stored one, and two readers of
 * the same list then disagree about what "one place earlier" means.
 *
 * Hidden columns are moved like any other. The picker lists them, greyed, in
 * their place, so they can be arranged before being switched on.
 *
 * @param columns - The screen's declared columns.
 * @param preset - The reader's stored layout.
 * @param key - The column to move.
 * @param toIndex - Where it should land, clamped into the list.
 * @returns The new layout, or the old one when there is nothing to move.
 */
export function moveColumn(
  columns: readonly ColumnIdentity[],
  preset: TableColumnPreset,
  key: string,
  toIndex: number
): TableColumnPreset {
  const laidOut = laidOutColumns(columns, preset).map(column => column.key)
  const from = laidOut.indexOf(key)
  if (from === -1) {
    return preset
  }

  const to = Math.min(Math.max(toIndex, 0), laidOut.length - 1)
  const frozen = new Set(frozenKeys(columns, preset))
  if (frozen.has(key) !== frozen.has(laidOut[to])) {
    return preset
  }
  if (to === from) {
    return preset
  }

  const order = [...laidOut]
  order.splice(from, 1)
  order.splice(to, 0, key)

  return { ...preset, order }
}
